import React from 'react';

import { Card, Box, Typography, IconButton, Divider, Tooltip } from '@mui/material';

import { User, Link, CircleDot, Wallet, Copy, Edit, ExternalLink } from 'lucide-react';

import { colors } from '../../../../theme';

const PartnerCard = ({
  name,
  logoUrl,
  websiteUrl,
  status,
  userAddress,
  onEdit
}) => {
  const initial = name ? name.charAt(0).toUpperCase() : 'P';

  return (
    <Card
      sx={{
        borderRadius: '16px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
        border: `1px solid ${colors.dashboard.cardBorder}`,
        bgcolor: colors.background.paper,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
        height: '100%',
      }}
    >
      {/* Status */}
      <Box
        sx={{
          position: 'absolute',
          top: 2,
          right: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          bgcolor: status
            ? colors.dashboard.status.activeBg
            : colors.dashboard.status.dangerBg,
          color: status
            ? colors.dashboard.status.activeText
            : colors.dashboard.status.dangerText,
          px: 1.5,
          py: 0.5,
          borderRadius: '999px',
          fontSize: '12px',
          fontWeight: 600,
          zIndex: 2,
        }}
      >
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: status
              ? colors.dashboard.status.activeText
              : colors.dashboard.status.dangerText,
          }}
        />

        {status ? 'Active' : 'Inactive'}
      </Box>

      {/* Card Content */}
      <Box
        sx={{
          p: 2,
          pb: 1.5,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          flexGrow: 1,
        }}
      >
        {/* Partner Logo */}
        <Box
          sx={{
            width: 72,
            height: 72,
            borderRadius: '16px',
            bgcolor: colors.slate[50],
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            mb: 1.5,
            mt: 1,
          }}
        >
          {logoUrl ? (
            <Box
              component="img"
              src={logoUrl}
              alt={name || 'Partner logo'}
              sx={{
                maxWidth: '80%',
                maxHeight: '80%',
                objectFit: 'contain',
                borderRadius: '12px',
              }}
            />
          ) : (
            <Typography
              variant="h4"
              sx={{
                color: colors.slate[400],
                fontWeight: 'bold',
              }}
            >
              {initial}
            </Typography>
          )}
        </Box>

        {/* Partner Name */}
        <Typography
          variant="h5"
          sx={{
            color: colors.dashboard.title,
            fontWeight: 800,
            mb: 2,
            textAlign: 'center',
            overflowWrap: 'break-word',
            wordBreak: 'break-word',
          }}
        >
          {name || 'Unknown'}
        </Typography>

        {/* Partner Details */}
        <Box
          sx={{
            width: '100%',
            display: 'grid',
            gridTemplateColumns: '100px 1fr',
            rowGap: 1.25,
            columnGap: 1,
          }}
        >
          {/* Name Label */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              color: colors.slate[500],
              gap: 1,
            }}
          >
            <User
              size={16}
              style={{
                marginTop: '2px',
                flexShrink: 0,
              }}
            />

            <Typography
              sx={{
                fontSize: '12px',
                fontWeight: 500,
                lineHeight: 1.4,
              }}
            >
              Name
            </Typography>
          </Box>

          {/* Name Value */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              minWidth: 0,
            }}
          >
            <Typography
              sx={{
                fontSize: '13px',
                color: colors.text.primary,
                fontWeight: 500,
                overflowWrap: 'break-word',
                wordBreak: 'break-word',
                lineHeight: 1.4,
              }}
            >
              {name || 'Unknown'}
            </Typography>
          </Box>

          {/* Website Label */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              color: colors.slate[500],
              gap: 1,
            }}
          >
            <Link
              size={16}
              style={{
                marginTop: '2px',
                flexShrink: 0,
              }}
            />

            <Typography
              sx={{
                fontSize: '12px',
                fontWeight: 500,
                lineHeight: 1.4,
              }}
            >
              Website URL
            </Typography>
          </Box>

          {/* Website Value */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              minWidth: 0,
            }}
          >
            {websiteUrl ? (
              <Typography
                component="a"
                href={websiteUrl}
                target="_blank"
                rel="noreferrer"
                sx={{
                  fontSize: '13px',
                  color: colors.primary.main,
                  fontWeight: 500,
                  textDecoration: 'none',
                  overflowWrap: 'break-word',
                  wordBreak: 'break-word',
                  lineHeight: 1.4,
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                {websiteUrl}

                <Box
                  component="span"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    ml: 0.5,
                    verticalAlign: 'middle',
                  }}
                >
                  <ExternalLink
                    size={14}
                    color={colors.primary.main}
                  />
                </Box>
              </Typography>
            ) : (
              <Typography
                sx={{
                  fontSize: '13px',
                  color: colors.text.primary,
                  fontWeight: 500,
                  lineHeight: 1.4,
                }}
              >
                N/A
              </Typography>
            )}
          </Box>

          {/* Wallet Label */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              color: colors.slate[500],
              gap: 1,
            }}
          >
            <Wallet
              size={16}
              style={{
                marginTop: '2px',
                flexShrink: 0,
              }}
            />

            <Typography
              sx={{
                fontSize: '12px',
                fontWeight: 500,
                lineHeight: 1.4,
              }}
            >
              Wallet Address
            </Typography>
          </Box>

          {/* Wallet Value */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1,
              minWidth: 0,
            }}
          >
            <Typography
              sx={{
                fontSize: '12px',
                color: colors.text.primary,
                overflowWrap: 'anywhere',
                wordBreak: 'break-word',
                lineHeight: 1.4,
                flexGrow: 1,
                minWidth: 0,
              }}
            >
              {userAddress || 'N/A'}
            </Typography>

            {userAddress && (
              <Tooltip title="Copy wallet address" placement="top" arrow>
                <IconButton
                  size="small"
                  onClick={() =>
                    navigator.clipboard.writeText(userAddress)
                  }
                  sx={{
                    p: 0,
                    color: colors.slate[400],
                    flexShrink: 0,
                    '&:hover': {
                      color: colors.primary.main,
                      bgcolor: colors.slate[50],
                    },
                  }}
                >
                  <Copy size={14} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
      </Box>

      {/* Card Actions */}
      <Divider
        sx={{
          borderColor: colors.dashboard.divider,
        }}
      />

      <Box
        sx={{
          bgcolor: colors.slate[50],
          px: 2,
          py: 1,
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <Tooltip
          title="Edit Partner"
          placement="top"
          arrow
        >
          <IconButton
            onClick={onEdit}
            sx={{
              bgcolor: colors.primary.lighter,
              color: colors.primary.main,
              borderRadius: '8px',
              p: 1,
              '&:hover': {
                bgcolor: colors.primary.light,
                color: colors.primary.contrastText,
              },
            }}
          >
            <Edit size={18} />
          </IconButton>
        </Tooltip>
      </Box>
    </Card>
  );
};

export default PartnerCard;